import {
  Box, LinearProgress, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { SessionSummary } from '../../api/claude';

function fmtK(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function usageColor(pct: number): 'success' | 'warning' | 'error' {
  if (pct >= 90) return 'error';
  if (pct >= 70) return 'warning';
  return 'success';
}

export function SessionUsageTable({ sessions }: { sessions: SessionSummary[] }) {
  if (sessions.length === 0) {
    return <Typography color="text.secondary" variant="body2">No sessions recorded yet.</Typography>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Session</TableCell>
          <TableCell>User</TableCell>
          <TableCell>Started</TableCell>
          <TableCell sx={{ minWidth: 200 }}>Usage</TableCell>
          <TableCell align="right">Tokens</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sessions.map((s) => {
          const pct = Math.min(s.percent_of_session_limit, 100);
          const color = usageColor(s.percent_of_session_limit);
          return (
            <TableRow key={s.session_id}>
              <TableCell>
                <Tooltip title={s.session_id}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {s.session_id.slice(0, 12)}…
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{s.username}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {new Date(s.started_at).toLocaleString()}
                </Typography>
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      color={color}
                      sx={{ height: 6, borderRadius: 1 }}
                    />
                  </Box>
                  <Typography variant="caption" color={`${color}.main`} sx={{ minWidth: 36 }}>
                    {s.percent_of_session_limit.toFixed(0)}%
                  </Typography>
                  {s.percent_of_session_limit >= 90 && (
                    <WarningAmberIcon fontSize="small" color="error" />
                  )}
                </Stack>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">{fmtK(s.total_tokens)}</Typography>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
